import { Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { motion } from 'framer-motion';
import { Check, Sparkles, X, Zap, Crown, Clock, MessageSquare, FileText, Instagram, Quote, Hash, Eye, Lock } from 'lucide-react';
import { useEffect } from 'react';
import useSEO from '@/hooks/useSEO';

const Pricing = () => {
  const { isAuthenticated, user } = useAuth();

  useSEO({
    title: 'Pricing — EpisodeKit',
    description: 'Simple, transparent pricing for creators turning podcasts and videos into transcripts, show notes, SEO blog posts, social posts, and more. Start free.',
    path: '/pricing',
    image: '/og/episodekit-og-pricing.jpg',
    index: true,
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Plan definitions derived directly from backend PLAN_CONFIG
  const plans = [
    {
      id: 'free',
      name: 'Free',
      price: 0,
      subtitle: 'Preview all AI outputs',
      description: 'See every AI tool in action. Upgrade to copy, export, and generate more.',
      limits: {
        episodes: '1 episode/month',
        minutes: '60 min/month',
        maxEpisodeLength: 'Max 60 min/episode'
      },
      features: [
        { text: 'AI transcription', included: true, icon: FileText },
        { text: 'Show notes generation', included: true, icon: FileText },
        { text: 'Blog post generation', included: true, icon: FileText },
        { text: 'Twitter & LinkedIn snippets', included: true, icon: MessageSquare },
        { text: 'Watermarked exports', included: true, note: 'watermark' },
        { text: 'Tone presets', included: false, preview: true },
        { text: 'ZIP export', included: false, preview: false },
        { text: 'TikTok/Instagram captions', included: false, preview: true },
        { text: 'Best quotes extraction', included: false, preview: true },
        { text: 'SEO title suggestions', included: false, preview: true },
        { text: 'YouTube outputs', included: false, preview: true },
        { text: 'Advanced instructions', included: false, preview: false },
      ],
      cta: 'Get Started',
      featured: false,
      checkoutUrl: null
    },
    {
      id: 'basic',
      name: 'Basic',
      price: 12,
      subtitle: 'For weekly podcasters',
      description: 'Ideal if you publish up to 1 episode per week.',
      limits: {
        episodes: '5 episodes/month',
        minutes: '300 min/month',
        maxEpisodeLength: 'Max 120 min/episode'
      },
      features: [
        { text: 'AI transcription', included: true, icon: FileText, access: 'Full access' },
        { text: 'Show notes generation', included: true, icon: FileText, access: 'Full access' },
        { text: 'Blog post generation', included: true, icon: FileText, access: 'Full access' },
        { text: 'Twitter & LinkedIn snippets', included: true, icon: MessageSquare, access: 'Copy & Export' },
        { text: 'No watermark', included: true, highlight: true },
        { text: '5 tone presets', included: true, icon: Zap, highlight: true },
        { text: 'ZIP export', included: true, highlight: true },
        { text: 'TikTok/Instagram captions', included: false },
        { text: 'Best quotes extraction', included: false },
        { text: 'SEO title suggestions', included: false },
        { text: 'YouTube outputs', included: false },
        { text: 'Advanced instructions', included: false },
      ],
      cta: 'Subscribe',
      featured: false,
      checkoutUrl: process.env.REACT_APP_LEMON_BASIC_CHECKOUT_URL
    },
    {
      id: 'pro',
      name: 'Pro',
      price: 29,
      subtitle: 'Most popular',
      description: 'All AI tools unlocked. Best for serious creators.',
      limits: {
        episodes: '15 episodes/month',
        minutes: '900 min/month',
        maxEpisodeLength: 'Max 120 min/episode'
      },
      features: [
        { text: 'AI transcription', included: true, icon: FileText, access: 'Full access' },
        { text: 'Show notes generation', included: true, icon: FileText, access: 'Full access' },
        { text: 'Blog post generation', included: true, icon: FileText, access: 'Full access' },
        { text: 'Twitter & LinkedIn snippets', included: true, icon: MessageSquare, access: 'Copy & Export' },
        { text: 'No watermark', included: true },
        { text: '5 tone presets', included: true, icon: Zap },
        { text: 'ZIP export', included: true },
        { text: 'TikTok captions', included: true, icon: Hash, highlight: true, pro: true, access: 'Full access' },
        { text: 'Instagram captions', included: true, icon: Instagram, highlight: true, pro: true, access: 'Full access' },
        { text: 'Best quotes with speaker', included: true, icon: Quote, highlight: true, pro: true, access: 'Full access' },
        { text: 'SEO title suggestions', included: true, highlight: true, pro: true, access: 'Full access' },
        { text: 'YouTube outputs', included: true, highlight: true, pro: true, access: 'Full access' },
        { text: 'Advanced instructions', included: true, icon: Crown, highlight: true, pro: true },
        { text: 'Priority processing', included: true },
      ],
      cta: 'Subscribe',
      featured: true,
      checkoutUrl: process.env.REACT_APP_LEMON_PRO_CHECKOUT_URL
    },
    {
      id: 'unlimited',
      name: 'Unlimited',
      price: 59,
      subtitle: 'For agencies & teams',
      description: 'No limits. Perfect for managing multiple shows.',
      limits: {
        episodes: 'Unlimited episodes',
        minutes: 'Unlimited minutes',
        maxEpisodeLength: 'Max 120 min/episode'
      },
      features: [
        { text: 'Everything in Pro', included: true, icon: Check, access: 'Full access' },
        { text: 'Unlimited episodes', included: true, highlight: true },
        { text: 'Unlimited processing', included: true, highlight: true },
        { text: 'Premium support', included: true, icon: Crown },
      ],
      cta: 'Subscribe',
      featured: false,
      checkoutUrl: process.env.REACT_APP_LEMON_UNLIMITED_CHECKOUT_URL
    }
  ];

  // Pro features highlight section
  const proFeatures = [
    {
      icon: Instagram,
      title: 'Social Captions',
      description: 'Generate optimized captions for TikTok and Instagram with hooks, hashtags, and CTAs.'
    },
    {
      icon: Quote,
      title: 'Best Quotes',
      description: 'Extract memorable quotes with speaker attribution and context for social sharing.'
    },
    {
      icon: Hash,
      title: 'SEO Titles',
      description: 'Get 5 SEO-optimized title suggestions to maximize discoverability.'
    },
    {
      icon: Crown,
      title: 'Advanced Instructions',
      description: 'Customize AI output with your own instructions for brand voice and style.'
    }
  ];

  const faqs = [
    { q: 'What counts as an episode?', a: 'An episode is any audio file you upload. Episodes over 60 minutes use proportionally more of your monthly minutes allowance.' },
    { q: 'What are the tone presets?', a: 'Basic+ plans include 5 tones: Neutral, Professional, Casual, Storytelling, and Humorous. These change the writing style of all generated content.' },
    { q: 'What are the Pro AI tools?', a: 'Pro unlocks: TikTok captions, Instagram captions, Best Quotes extraction (with speaker), SEO title suggestions, YouTube outputs (description, SEO titles, chapters, pinned comment, Shorts hooks, SRT subtitles), and Advanced Instructions for custom prompts.' },
    { q: 'Can I cancel anytime?', a: 'Yes, cancel anytime. Access continues until the end of your billing period.' },
    { q: 'What happens if payment fails?', a: 'Your account enters "past due" status. You can still view existing episodes but cannot create new ones until payment is updated.' },
    { q: 'What file formats are supported?', a: 'MP3, WAV, M4A, FLAC, OGG, MP4, WebM, and MOV. No file size limit thanks to cloud storage.' }
  ];

  const handleCheckout = (plan) => {
    if (!plan.checkoutUrl) return;
    
    const url = new URL(plan.checkoutUrl);
    
    if (user?.email) {
      url.searchParams.append('checkout[email]', user.email);
    }
    
    url.searchParams.append('checkout[custom][plan_id]', plan.id);
    url.searchParams.append('checkout[custom][user_email]', user?.email || '');
    
    // Use window.open or direct assignment
    window.open(url.toString(), '_self');
  };

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="pricing-page">
      <div className="noise-overlay" />
      <Navbar />

      <section className="pt-32 pb-24 px-6 md:px-12">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="text-center mb-16">
            <motion.p 
              className="text-primary uppercase text-xs tracking-widest mb-4 font-semibold"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              Simple, Honest Pricing
            </motion.p>
            <motion.p
              className="text-sm text-neutral-500 mb-3 max-w-xl mx-auto"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.05 }}
            >
              12+ outputs. One upload. Under 5 minutes.
            </motion.p>
            <motion.h1 
              className="text-4xl md:text-5xl font-unbounded font-bold mb-4"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
            >
              Choose Your Plan
            </motion.h1>
            <motion.p 
              className="text-neutral-400 max-w-2xl mx-auto mb-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              Start free, upgrade when you need more. All plans include core AI features.
            </motion.p>
            <motion.p
              className="text-sm text-neutral-500 max-w-2xl mx-auto"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.25 }}
            >
              Comparing platforms? See our guide to the{' '}
              <Link
                to="/blog/best-podcast-repurposing-tools-2026"
                className="text-purple-300 hover:text-white underline underline-offset-4 decoration-purple-500/40 hover:decoration-purple-300"
                data-testid="pricing-link-best-tools-2026"
              >
                best podcast repurposing tools in 2026
              </Link>
              .
            </motion.p>
          </div>

          {/* Plan Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 pt-6">
            {plans.map((plan, index) => {
              const isCurrent = isAuthenticated && user?.plan === plan.id;
              return (
                <motion.div
                  key={plan.id}
                  className={`pricing-card ${plan.featured ? 'featured' : ''}`}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 * index }}
                  data-testid={`pricing-card-${plan.id}`}
                >
                  {plan.featured && (
                    <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                      <span className="badge bg-gradient-to-r from-[#7C3AED] to-[#06B6D4] text-white px-4 py-1 flex items-center gap-1 font-medium text-sm shadow-lg">
                        <Sparkles className="w-3 h-3" />
                        Most Popular
                      </span>
                    </div>
                  )}

                  <div className="mb-4">
                    <h3 className="text-xl font-semibold mb-1">{plan.name}</h3>
                    <p className="text-neutral-500 text-sm">{plan.subtitle}</p>
                  </div>

                  <div className="mb-4">
                    <span className="text-4xl font-unbounded font-bold">€{plan.price}</span>
                    <span className="text-neutral-500">/month</span>
                  </div>

                  <p className="text-neutral-400 text-xs mb-4 leading-relaxed min-h-[36px]">
                    {plan.description}
                  </p>

                  {/* Limits section */}
                  <div className="mb-4 p-3 bg-neutral-900/50 rounded-lg space-y-1">
                    <div className="flex items-center gap-2 text-xs">
                      <Clock className="w-3 h-3 text-primary" />
                      <span className="text-neutral-300">{plan.limits.episodes}</span>
                    </div>
                    <div className="flex items-center gap-2 text-xs">
                      <Zap className="w-3 h-3 text-primary" />
                      <span className="text-neutral-300">{plan.limits.minutes}</span>
                    </div>
                    <div className="flex items-center gap-2 text-xs">
                      <FileText className="w-3 h-3 text-primary" />
                      <span className="text-neutral-300">{plan.limits.maxEpisodeLength}</span>
                    </div>
                    {/* Language display removed - all content is English only (Task 1) */}
                  </div>

                  {/* Features list */}
                  <ul className="space-y-2 mb-6">
                    {plan.features.map((feature, i) => (
                      <li 
                        key={i} 
                        className={`flex items-center gap-2 text-sm ${
                          feature.included 
                            ? feature.highlight 
                              ? 'text-primary font-medium' 
                              : 'text-neutral-300' 
                            : 'text-neutral-600'
                        }`}
                      >
                        {feature.included ? (
                          feature.icon ? (
                            <feature.icon className={`w-4 h-4 flex-shrink-0 ${feature.pro ? 'text-primary' : 'text-success'}`} />
                          ) : (
                            <Check className="w-4 h-4 text-success flex-shrink-0" />
                          )
                        ) : (
                          <X className="w-4 h-4 text-neutral-700 flex-shrink-0" />
                        )}
                        <span>{feature.text}</span>
                        {feature.pro && (
                          <span className="text-[10px] px-1.5 py-0.5 bg-primary/20 text-primary rounded">PRO</span>
                        )}
                      </li>
                    ))}
                  </ul>

                  {plan.id === 'free' && (
                    <div className="flex justify-center mt-3 mb-1">
                      <span className="inline-block rounded-full bg-white/10 text-white/80 text-xs px-3 py-1">
                        No credit card needed
                      </span>
                    </div>
                  )}

                  {isCurrent ? (
                    <Button 
                      className="w-full btn-secondary" 
                      disabled
                      data-testid={`plan-${plan.id}-current`}
                    >
                      Current Plan
                    </Button>
                  ) : plan.id === 'free' ? (
                    isAuthenticated ? (
                      <Button 
                        className="w-full btn-ghost border border-neutral-700" 
                        disabled
                      >
                        Included
                      </Button>
                    ) : (
                      <Link to="/register">
                        <Button className="w-full btn-secondary" data-testid="free-plan-btn">
                          Get Started
                        </Button>
                      </Link>
                    )
                  ) : !isAuthenticated ? (
                    <Link to="/register">
                      <Button 
                        className={`w-full ${plan.featured ? 'btn-primary' : 'btn-secondary'}`}
                        data-testid={`subscribe-${plan.id}-btn`}
                      >
                        Sign Up to Subscribe
                      </Button>
                    </Link>
                  ) : (
                    <Button
                      onClick={() => handleCheckout(plan)}
                      className={`w-full ${plan.featured ? 'btn-primary' : 'btn-secondary'}`}
                      data-testid={`subscribe-${plan.id}-btn`}
                    >
                      Subscribe
                    </Button>
                  )}
                </motion.div>
              );
            })}
          </div>

          {/* Pro Features Highlight */}
          <motion.div 
            className="mt-20 p-8 rounded-2xl bg-gradient-to-br from-primary/10 to-cyan-500/10 border border-primary/20"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-2xl font-unbounded font-bold text-center mb-2">
              Pro AI Tools
            </h2>
            <p className="text-neutral-400 text-center mb-8 max-w-xl mx-auto">
              Unlock advanced AI capabilities with Pro or Unlimited plans
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {proFeatures.map((feature, i) => (
                <div key={i} className="text-center">
                  <div className="w-12 h-12 mx-auto mb-3 rounded-xl bg-primary/20 flex items-center justify-center">
                    <feature.icon className="w-6 h-6 text-primary" />
                  </div>
                  <h3 className="font-semibold mb-1">{feature.title}</h3>
                  <p className="text-neutral-400 text-sm">{feature.description}</p>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Feature Comparison Table */}
          <motion.div 
            className="mt-20"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-2xl font-unbounded font-bold text-center mb-8">
              Feature Comparison
            </h2>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-neutral-800">
                    <th className="text-left p-3 font-semibold">Feature</th>
                    <th className="text-center p-3 font-semibold">Free</th>
                    <th className="text-center p-3 font-semibold">Basic</th>
                    <th className="text-center p-3 font-semibold text-primary">Pro</th>
                    <th className="text-center p-3 font-semibold">Unlimited</th>
                  </tr>
                </thead>
                <tbody className="text-neutral-400">
                  <tr className="border-b border-neutral-800/50">
                    <td className="p-3">Episodes per month</td>
                    <td className="text-center p-3">1</td>
                    <td className="text-center p-3">5</td>
                    <td className="text-center p-3 text-primary">15</td>
                    <td className="text-center p-3">∞</td>
                  </tr>
                  <tr className="border-b border-neutral-800/50">
                    <td className="p-3">Minutes per month</td>
                    <td className="text-center p-3">60</td>
                    <td className="text-center p-3">300</td>
                    <td className="text-center p-3 text-primary">900</td>
                    <td className="text-center p-3">∞</td>
                  </tr>
                  <tr className="border-b border-neutral-800/50">
                    <td className="p-3">Max episode length</td>
                    <td className="text-center p-3">60 min</td>
                    <td className="text-center p-3">120 min</td>
                    <td className="text-center p-3 text-primary">120 min</td>
                    <td className="text-center p-3">120 min</td>
                  </tr>
                  <tr className="border-b border-neutral-800/50">
                    <td className="p-3">AI Transcription</td>
                    <td className="text-center p-3"><Check className="w-4 h-4 mx-auto text-success" /></td>
                    <td className="text-center p-3"><Check className="w-4 h-4 mx-auto text-success" /></td>
                    <td className="text-center p-3"><Check className="w-4 h-4 mx-auto text-success" /></td>
                    <td className="text-center p-3"><Check className="w-4 h-4 mx-auto text-success" /></td>
                  </tr>
                  <tr className="border-b border-neutral-800/50">
                    <td className="p-3">Show Notes & Blog</td>
                    <td className="text-center p-3"><Check className="w-4 h-4 mx-auto text-success" /></td>
                    <td className="text-center p-3"><Check className="w-4 h-4 mx-auto text-success" /></td>
                    <td className="text-center p-3"><Check className="w-4 h-4 mx-auto text-success" /></td>
                    <td className="text-center p-3"><Check className="w-4 h-4 mx-auto text-success" /></td>
                  </tr>
                  <tr className="border-b border-neutral-800/50">
                    <td className="p-3">Twitter & LinkedIn</td>
                    <td className="text-center p-3"><Check className="w-4 h-4 mx-auto text-success" /></td>
                    <td className="text-center p-3"><Check className="w-4 h-4 mx-auto text-success" /></td>
                    <td className="text-center p-3"><Check className="w-4 h-4 mx-auto text-success" /></td>
                    <td className="text-center p-3"><Check className="w-4 h-4 mx-auto text-success" /></td>
                  </tr>
                  <tr className="border-b border-neutral-800/50">
                    <td className="p-3">No Watermark</td>
                    <td className="text-center p-3"><X className="w-4 h-4 mx-auto text-neutral-600" /></td>
                    <td className="text-center p-3"><Check className="w-4 h-4 mx-auto text-success" /></td>
                    <td className="text-center p-3"><Check className="w-4 h-4 mx-auto text-success" /></td>
                    <td className="text-center p-3"><Check className="w-4 h-4 mx-auto text-success" /></td>
                  </tr>
                  <tr className="border-b border-neutral-800/50">
                    <td className="p-3">Tone Presets (5)</td>
                    <td className="text-center p-3"><X className="w-4 h-4 mx-auto text-neutral-600" /></td>
                    <td className="text-center p-3"><Check className="w-4 h-4 mx-auto text-success" /></td>
                    <td className="text-center p-3"><Check className="w-4 h-4 mx-auto text-success" /></td>
                    <td className="text-center p-3"><Check className="w-4 h-4 mx-auto text-success" /></td>
                  </tr>
                  <tr className="border-b border-neutral-800/50">
                    <td className="p-3">ZIP Export</td>
                    <td className="text-center p-3"><X className="w-4 h-4 mx-auto text-neutral-600" /></td>
                    <td className="text-center p-3"><Check className="w-4 h-4 mx-auto text-success" /></td>
                    <td className="text-center p-3"><Check className="w-4 h-4 mx-auto text-success" /></td>
                    <td className="text-center p-3"><Check className="w-4 h-4 mx-auto text-success" /></td>
                  </tr>
                  {/* Regenerations row removed - Task 2 */}
                  <tr className="border-b border-neutral-800/50 bg-primary/5">
                    <td className="p-3 font-medium">TikTok Captions</td>
                    <td className="text-center p-3"><X className="w-4 h-4 mx-auto text-neutral-600" /></td>
                    <td className="text-center p-3"><X className="w-4 h-4 mx-auto text-neutral-600" /></td>
                    <td className="text-center p-3"><Check className="w-4 h-4 mx-auto text-primary" /></td>
                    <td className="text-center p-3"><Check className="w-4 h-4 mx-auto text-primary" /></td>
                  </tr>
                  <tr className="border-b border-neutral-800/50 bg-primary/5">
                    <td className="p-3 font-medium">Instagram Captions</td>
                    <td className="text-center p-3"><X className="w-4 h-4 mx-auto text-neutral-600" /></td>
                    <td className="text-center p-3"><X className="w-4 h-4 mx-auto text-neutral-600" /></td>
                    <td className="text-center p-3"><Check className="w-4 h-4 mx-auto text-primary" /></td>
                    <td className="text-center p-3"><Check className="w-4 h-4 mx-auto text-primary" /></td>
                  </tr>
                  <tr className="border-b border-neutral-800/50 bg-primary/5">
                    <td className="p-3 font-medium">Best Quotes</td>
                    <td className="text-center p-3"><X className="w-4 h-4 mx-auto text-neutral-600" /></td>
                    <td className="text-center p-3"><X className="w-4 h-4 mx-auto text-neutral-600" /></td>
                    <td className="text-center p-3"><Check className="w-4 h-4 mx-auto text-primary" /></td>
                    <td className="text-center p-3"><Check className="w-4 h-4 mx-auto text-primary" /></td>
                  </tr>
                  <tr className="border-b border-neutral-800/50 bg-primary/5">
                    <td className="p-3 font-medium">SEO Titles</td>
                    <td className="text-center p-3"><X className="w-4 h-4 mx-auto text-neutral-600" /></td>
                    <td className="text-center p-3"><X className="w-4 h-4 mx-auto text-neutral-600" /></td>
                    <td className="text-center p-3"><Check className="w-4 h-4 mx-auto text-primary" /></td>
                    <td className="text-center p-3"><Check className="w-4 h-4 mx-auto text-primary" /></td>
                  </tr>
                  <tr className="border-b border-neutral-800/50 bg-primary/5">
                    <td className="p-3 font-medium">YouTube Outputs</td>
                    <td className="text-center p-3"><X className="w-4 h-4 mx-auto text-neutral-600" /></td>
                    <td className="text-center p-3"><X className="w-4 h-4 mx-auto text-neutral-600" /></td>
                    <td className="text-center p-3"><Check className="w-4 h-4 mx-auto text-primary" /></td>
                    <td className="text-center p-3"><Check className="w-4 h-4 mx-auto text-primary" /></td>
                  </tr>
                  <tr className="border-b border-neutral-800/50 bg-primary/5">
                    <td className="p-3 font-medium">Advanced Instructions</td>
                    <td className="text-center p-3"><X className="w-4 h-4 mx-auto text-neutral-600" /></td>
                    <td className="text-center p-3"><X className="w-4 h-4 mx-auto text-neutral-600" /></td>
                    <td className="text-center p-3"><Check className="w-4 h-4 mx-auto text-primary" /></td>
                    <td className="text-center p-3"><Check className="w-4 h-4 mx-auto text-primary" /></td>
                  </tr>
                </tbody>
              </table>
            </div>
          </motion.div>

          {/* Notes */}
          <div className="text-center mt-12">
            <p className="text-neutral-400 text-sm max-w-2xl mx-auto">
              * One episode = up to 60 minutes of audio. Longer episodes use proportionally more minutes.
            </p>
            <p className="text-neutral-500 text-sm mt-4">
              Secure payments via Lemon Squeezy. Cancel anytime.
            </p>
          </div>

          {/* FAQs */}
          <div className="mt-24">
            <h2 className="text-2xl font-unbounded font-bold text-center mb-12">Frequently Asked Questions</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
              {faqs.map((faq, i) => (
                <motion.div
                  key={i}
                  className="card-base"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                >
                  <h3 className="font-semibold mb-2">{faq.q}</h3>
                  <p className="text-neutral-400 text-sm">{faq.a}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Pricing;
